import { Anchor, Group, Text } from '@mantine/core'
import { IconCheck } from '@tabler/icons-react'
import {
  formatDate,
  formatDateTime,
  formatMoney,
  formatNumber,
  formatPhone,
  formatTaxId,
  formatTime,
  type Locale,
  labels,
  type RefMap,
  type ResolvedField,
  type Resource,
} from '@tnn/core'
import { StatusPill } from '@tnn/ui'
import type { TFunction } from 'i18next'
import type { ReactNode } from 'react'
import { PersonAvatar } from '../photo/photos'

/** Records of these resources are people or companies: show an avatar next to their name. */
const AVATAR_RESOURCES = new Set(['accounting.customer', 'tms.driver', 'admin.user', 'hr.employee'])

export interface FieldValueContext {
  resource: Resource
  refs: RefMap
  t: TFunction
  locale: Locale
}

export function refLabel(field: ResolvedField, value: unknown, refs: RefMap): string {
  if (!field.ref || value == null || value === '') return ''
  return refs[field.ref.resource]?.[String(value)] ?? String(value)
}

/** Plain-text value, used by exports, cards and print. */
export function fieldText(field: ResolvedField, value: unknown, { resource, refs, t, locale }: FieldValueContext): string {
  if (value == null || value === '') return ''
  if (field.ref) {
    if (Array.isArray(value)) return value.map((v) => refLabel(field, v, refs)).join(', ')
    return refLabel(field, value, refs)
  }
  switch (field.type) {
    case 'select':
      return t(labels.option(resource, field.name, String(value)), { defaultValue: String(value) })
    case 'multiselect':
      return (value as string[]).map((v) => t(labels.option(resource, field.name, v), { defaultValue: v })).join(', ')
    case 'money':
      return formatMoney(Number(value), { locale })
    case 'number':
    case 'integer':
      return formatNumber(Number(value), { locale })
    case 'percent':
      return `${formatNumber(Number(value), { locale })}%`
    case 'date':
      return formatDate(String(value), { locale })
    case 'datetime':
      return formatDateTime(String(value), { locale })
    case 'time':
      return formatTime(String(value))
    case 'boolean':
      return value ? t('form.yes') : t('form.no')
    case 'taxId':
      return formatTaxId(String(value))
    case 'phone':
      return formatPhone(String(value))
    case 'tags':
      return (value as string[]).join(', ')
    case 'json':
      return ''
    default:
      return String(value)
  }
}

/** Rich value for tables and details: status pills, avatars, links. */
export function FieldValue({ field, value, ctx }: { field: ResolvedField; value: unknown; ctx: FieldValueContext }): ReactNode {
  if (value == null || value === '' || (Array.isArray(value) && value.length === 0)) {
    return (
      <Text span c="dimmed">
        –
      </Text>
    )
  }
  if (field.tones && field.type === 'select') {
    const tone = field.tones[String(value)] ?? 'neutral'
    return (
      <StatusPill
        tone={tone}
        label={fieldText(field, value, ctx)}
        icon={tone === 'neutral' && value === 'delivered' ? IconCheck : undefined}
      />
    )
  }
  if (field.ref && !Array.isArray(value)) {
    const label = refLabel(field, value, ctx.refs)
    if (AVATAR_RESOURCES.has(field.ref.resource)) {
      return (
        <Group gap={10} wrap="nowrap">
          <PersonAvatar
            name={label}
            size={28}
            userId={field.ref.resource === 'admin.user' ? String(value ?? '') : null}
            employeeId={field.ref.resource === 'hr.employee' ? String(value ?? '') : null}
          />
          <Text span size="sm" truncate>
            {label}
          </Text>
        </Group>
      )
    }
    return label
  }
  if (field.type === 'boolean') {
    return value ? (
      <IconCheck size={18} stroke={2} color="var(--tnn-primary)" aria-label={ctx.t('form.yes')} />
    ) : (
      <Text span c="dimmed">
        –
      </Text>
    )
  }
  if (field.type === 'phone') {
    return (
      <Anchor href={`tel:${String(value).replace(/\D/g, '')}`} size="sm" onClick={(e) => e.stopPropagation()}>
        {formatPhone(String(value))}
      </Anchor>
    )
  }
  if (field.type === 'email') {
    return (
      <Anchor href={`mailto:${String(value)}`} size="sm" onClick={(e) => e.stopPropagation()}>
        {String(value)}
      </Anchor>
    )
  }
  return fieldText(field, value, ctx)
}
